// AlertBanner.jsx
// Alert banner for critical device states
// Part of Device Management Overhaul - Phase 5: UI/UX Polish

import React, { memo, useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Button,
  Collapse,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon
} from '@mui/icons-material';

const AlertBanner = memo(({
  devices,
  onRefresh,
  onFilterOffline,
  onFilterUnlinked,
  dismissible = true,
  autoRefresh = false,
  refreshInterval = 30000
}) => {
  const [dismissed, setDismissed] = React.useState({});
  // Use ref for progress to avoid re-renders - only update DOM directly
  const progressRef = React.useRef(0);
  const progressBarRef = React.useRef(null);

  // Calculate device statistics
  const stats = useMemo(() => {
    const total = devices.length;
    const offline = devices.filter(d => {
      const status = (d.config?.status || d.status || '').toLowerCase();
      return ['offline', 'inactive', 'disconnected'].includes(status);
    }).length;

    const unlinked = devices.filter(d =>
      !d.parentDevice &&
      !d.parentDeviceId &&
      !d.locationModel &&
      !d.deviceLocation
    ).length;

    const critical = devices.filter(d => {
      if (!d.last_seen) return false;
      const hours = (new Date() - new Date(d.last_seen)) / (1000 * 60 * 60);
      return hours > 24;
    }).length;

    return {
      total,
      offline,
      unlinked,
      critical,
      offlinePercent: total > 0 ? Math.round((offline / total) * 100) : 0,
      unlinkedPercent: total > 0 ? Math.round((unlinked / total) * 100) : 0
    };
  }, [devices]);

  // Auto-refresh logic - use refs to avoid re-renders
  React.useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      progressRef.current += (100 / (refreshInterval / 1000));

      // Update progress bar directly via DOM to avoid re-renders
      if (progressBarRef.current) {
        const bar = progressBarRef.current.querySelector('.MuiLinearProgress-bar');
        if (bar) {
          bar.style.transform = `translateX(${progressRef.current - 100}%)`;
        }
      }

      if (progressRef.current >= 100) {
        progressRef.current = 0;
        // Reset progress bar
        if (progressBarRef.current) {
          const bar = progressBarRef.current.querySelector('.MuiLinearProgress-bar');
          if (bar) {
            bar.style.transform = 'translateX(-100%)';
          }
        }
        // Schedule the refresh
        setTimeout(() => onRefresh?.(), 0);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, onRefresh]);

  // Determine alerts to show
  const alerts = useMemo(() => {
    const alertList = [];

    // Critical offline alert (>50% devices offline or >10 critical devices)
    if (stats.offlinePercent > 50 || stats.critical > 10) {
      alertList.push({
        id: 'critical-offline',
        severity: 'error',
        title: 'Critical: Multiple Devices Offline',
        message: `${stats.offline} devices (${stats.offlinePercent}%) are currently offline. ${stats.critical} devices haven't been seen for over 24 hours.`,
        icon: <ErrorIcon />,
        action: {
          label: 'View Offline Devices',
          onClick: onFilterOffline
        }
      });
    }
    // Warning for moderate offline (>20%)
    else if (stats.offlinePercent > 20) {
      alertList.push({
        id: 'warning-offline',
        severity: 'warning',
        title: 'Warning: Devices Offline',
        message: `${stats.offline} devices (${stats.offlinePercent}%) are currently offline.`,
        icon: <WarningIcon />,
        action: {
          label: 'View Offline Devices',
          onClick: onFilterOffline
        }
      });
    }

    // Unlinked devices alert (>10% or >20 devices)
    if (stats.unlinkedPercent > 10 || stats.unlinked > 20) {
      alertList.push({
        id: 'unlinked-devices',
        severity: 'warning',
        title: 'Unlinked Devices Detected',
        message: `${stats.unlinked} devices (${stats.unlinkedPercent}%) are not linked to any location or parent device.`,
        icon: <UnlinkIcon />,
        action: {
          label: 'View Unlinked Devices',
          onClick: onFilterUnlinked
        }
      });
    }

    // Info alert for good health
    if (stats.offlinePercent < 5 && stats.unlinkedPercent < 5 && stats.total > 0) {
      alertList.push({
        id: 'healthy-network',
        severity: 'success',
        title: 'Network Health: Good',
        message: `${stats.total - stats.offline} of ${stats.total} devices are online and operational.`,
        icon: <InfoIcon />,
        dismissible: true
      });
    }

    return alertList.filter(alert => !dismissed[alert.id]);
  }, [stats, dismissed, onFilterOffline, onFilterUnlinked]);

  if (alerts.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {alerts.map((alert, index) => (
        <Collapse key={alert.id} in={!dismissed[alert.id]}>
          <Alert
            severity={alert.severity}
            icon={alert.icon}
            sx={{
              mb: index < alerts.length - 1 ? 1 : 0,
              '& .MuiAlert-message': { width: '100%' }
            }}
            action={
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {alert.action && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={alert.action.onClick}
                    sx={{
                      borderColor: 'inherit',
                      color: 'inherit',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    {alert.action.label}
                  </Button>
                )}
                {autoRefresh && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      progressRef.current = 0;
                      if (progressBarRef.current) {
                        const bar = progressBarRef.current.querySelector('.MuiLinearProgress-bar');
                        if (bar) {
                          bar.style.transform = 'translateX(-100%)';
                        }
                      }
                      onRefresh?.();
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                )}
                {dismissible && (
                  <IconButton
                    size="small"
                    onClick={() => setDismissed(prev => ({ ...prev, [alert.id]: true }))}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Box>
            }
          >
            <AlertTitle>{alert.title}</AlertTitle>
            <Typography variant="body2">{alert.message}</Typography>
            {autoRefresh && alert.severity === 'error' && (
              <LinearProgress
                ref={progressBarRef}
                variant="determinate"
                value={0}
                sx={{
                  mt: 1,
                  height: 2,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'currentColor',
                    transition: 'none' // Disable MUI transition for manual control
                  }
                }}
              />
            )}
          </Alert>
        </Collapse>
      ))}
    </Box>
  );
});

AlertBanner.displayName = 'AlertBanner';

// Simple unlinked device warning component
export const UnlinkedDeviceWarning = memo(({ device }) => {
  if (device?.parentDevice || device?.locationModel) return null;

  return (
    <Alert
      severity="info"
      icon={<UnlinkIcon />}
      sx={{ mt: 1 }}
    >
      <Typography variant="body2">
        This device is not linked to any location or parent device.
        Consider assigning it to maintain proper network hierarchy.
      </Typography>
    </Alert>
  );
});

UnlinkedDeviceWarning.displayName = 'UnlinkedDeviceWarning';

export default AlertBanner;